import Phaser from 'phaser';
import type { PlaySceneData, ResultSceneData } from './scene-data';
import { KeeperSprite } from '@/game/KeeperSprite';
import type { DailyRecord, LocalStateStore, ShotResult } from '@/lib/local-state';
import { isComplete } from '@/lib/local-state';
import type { Zone } from '@/lib/zones';
import { mulberry32, sampleKeeperZone, applyMoodShift, reconstructDistribution } from '@/lib/keeper';
import { aimToZone, type SwipeVector } from '@/lib/swipe-math';
import { ballTarget } from '@/lib/ball-target';
import { resolveShot } from '@/lib/shot-resolution';
import { SHOT_TIMING_MS, reducedMotionScale } from '@/lib/animation-timing';
import { gameBus } from '@/bridge/gameBus';
import { puzzleNumber } from '@/lib/launch';
import { SCHEMA_VERSION, SHOTS_PER_DAY } from '@/lib/constants';
import { t } from '@/i18n';
import { archetypeTier, type ZoneDistribution } from '@/lib/archetypes';
import { audio } from '@/audio-singleton';
import { resolveGameConfig, type GameConfig } from '@/lib/game-mode';
import { lowestProbZone } from '@/lib/shot-logic';
import { assistEligible, assistUsedToday } from '@/lib/assist';
import { uiCommandBus, type UiCommand } from '@/bridge/uiCommandBus';
import type { UiPrefs } from '@/lib/ui-prefs';

export type PlayState = 'IDLE' | 'AIMING' | 'RESOLVING' | 'BETWEEN_SHOTS' | 'COMPLETE';

const _ZONE_GRID: readonly (readonly Zone[])[] = [
  ['LA', 'CA', 'RA'], // top row (Above)
  ['LF', 'CF', 'RF'], // bottom row (Front)
];

const COLOR_GOAL = 0x22cc55;
const COLOR_MISS = 0x222222;
const COLOR_PENDING = 0x666666;
const COLOR_ZONE = 0xffffff;
const COLOR_BALL = 0xffffff;

export class PlayScene extends Phaser.Scene {
  private store!: LocalStateStore;
  private playData!: PlaySceneData;
  private state: PlayState = 'IDLE';
  private prng!: () => number;
  private keeperDist!: ZoneDistribution;
  private moodDelta!: number;
  private config!: GameConfig;
  private uiPrefs?: UiPrefs;
  private practiceRevealed = false;
  private practiceShots: ShotResult[] = [];
  private returnTo?: ResultSceneData;
  private commandUnsub?: () => void;
  private indicatorRects: Phaser.GameObjects.Rectangle[] = [];
  private keeperSprite!: KeeperSprite;
  private ballCircle!: Phaser.GameObjects.Arc;
  private currentShotIndex = 0;

  private aimLine?: Phaser.GameObjects.Graphics;
  private aimStart: { x: number; y: number } | null = null;
  private aimStartTime = 0;
  private motionScale: 0 | 1 = 1;
  private observations: Record<Zone, number> = { LF: 0, CF: 0, RF: 0, LA: 0, CA: 0, RA: 0 };
  private pulseTimer?: Phaser.Time.TimerEvent;

  private static readonly MIN_POWER_PX = 60;

  constructor() {
    super({ key: 'PlayScene' });
  }

  init(data: PlaySceneData): void {
    this.playData = data;
    // §3.6: reset per-launch practice/return state so a recycled scene instance
    // can't carry a previous round's returnTo/practice flags into a new launch.
    // Conditional set/delete (not `= data.returnTo`) so an absent returnTo clears
    // any stale value without writing `undefined` (exactOptionalPropertyTypes).
    if (data.returnTo) this.returnTo = data.returnTo;
    else delete this.returnTo;
    this.practiceShots = [...data.existingShots];
    this.practiceRevealed = false;
    this.currentShotIndex = data.existingShots.length;
    this.prng = mulberry32(Date.now() ^ Math.floor(Math.random() * 2 ** 32));

    const tier = archetypeTier(data.archetype);
    const uiPrefs = this.registry?.get?.('uiPrefs') as { getAssistEnabled(): boolean } | undefined;
    // Plan #1 §4.2: config is the single source of moodDelta/mode/flags.
    // Existing launch sites (MenuScene/BootScene) pass `mode`; default 'daily'.
    this.config = data.config
      ?? resolveGameConfig({ mode: data.mode ?? 'daily', tier, assistEnabled: uiPrefs?.getAssistEnabled() ?? false });
    this.moodDelta = this.config.moodDelta;
    this.keeperDist = reconstructDistribution(data.archetype, data.existingShots, this.moodDelta);

    // Plan #5 §4.2: rebuild per-zone observation counts from the authoritative
    // shot log so bfcache restores resume with the correct callout state.
    this.observations = { LF: 0, CF: 0, RF: 0, LA: 0, CA: 0, RA: 0 };
    for (const s of data.existingShots) this.observations[s.keeperZone]++;
  }

  create(): void {
    this.store = this.registry.get('store') as LocalStateStore;
    this.uiPrefs = this.registry.get('uiPrefs') as UiPrefs;
    const { width, height } = this.scale;
    this.drawIndicatorStrip(width, height);
    this.drawGoalArea(width, height);
    this.drawBall(width, height);

    // Spec §3.2: read settings once at scene create — mid-shot toggles defer.
    this.motionScale = reducedMotionScale();

    const record: DailyRecord = {
      schemaVersion: SCHEMA_VERSION,
      shots: this.playData.existingShots,
      score: this.playData.existingShots.filter((s) => s.outcome === 'goal').length,
      shareGenerated: false,
    };
    gameBus.emit({
      type: 'scene:enter',
      payload: {
        scene: 'PLAY',
        date: this.playData.date,
        puzzleNumber: puzzleNumber(this.playData.date),
        archetype: this.playData.archetype,
        record,
      },
    });
    // Emit AFTER scene:enter — resetSceneScoped (on scene:enter) would clobber
    // an earlier play:mode (Plan #1 §3.5 / §4.1). config is set in init().
    gameBus.emit({
      type: 'play:mode',
      payload: { mode: this.config.mode, hideHeatmap: this.config.hideHeatmap },
    });
    this.resubscribeToCommands();
    this.recomputeAssist();

    this.setPlayState('IDLE');
    this.input.on('pointerdown', this.onPointerDown, this);
    this.input.on('pointermove', this.onPointerMove, this);
    this.input.on('pointerup', this.onPointerUp, this);
    this.input.on('pointerupoutside', this.onPointerCancel, this);
    this.registerPageShow();
    // Plan #5 §4.8 (review): defense-in-depth — cancel any in-flight pulse
    // timer on scene shutdown so a stale {zone:null} can't leak into a fresh
    // PlayScene's subscribers if Phaser's TimeManager teardown spares the cb.
    this.events.once('shutdown', () => {
      this.pulseTimer?.remove();
      // Single command-bus cleanup point (see resubscribeToCommands note):
      // reads the latest subscription so bfcache re-subscribes don't leak.
      this.commandUnsub?.();
      delete this.commandUnsub;
    });
  }

  private setPlayState(next: PlayState): void {
    this.state = next;
    gameBus.emit({ type: 'play:state', payload: { state: next, shotIndex: this.currentShotIndex } });
  }

  private pageShowHandler?: (e: PageTransitionEvent) => void;

  private registerPageShow(): void {
    this.pageShowHandler = (e: PageTransitionEvent) => {
      if (!e.persisted) return;
      // §3.6: a frozen practice round never persisted — restore to the
      // AUTHORITATIVE daily ResultScene via returnTo, NOT store reconstruction.
      if (this.config?.mode === 'practice' && this.returnTo) {
        this.scene.start('ResultScene', this.returnTo);
        return;
      }
      // Per spec §4.1: re-emit the AUTHORITATIVE record from the store, not from
      // playData.existingShots (which is the init-time snapshot — stale if the player
      // landed shots before the bfcache freeze).
      const fallback: DailyRecord = {
        schemaVersion: SCHEMA_VERSION,
        shots: this.playData.existingShots,
        score: this.playData.existingShots.filter((s) => s.outcome === 'goal').length,
        shareGenerated: false,
      };
      const record = this.store.getDailyRecord(this.playData.date) ?? fallback;
      // Plan #5 §3.4: bfcache restore must replay the round to reconstruct the
      // mid-round keeper distribution from the authoritative shot log (the
      // `record` from the store, NOT `playData.existingShots` which is the
      // stale init-time snapshot).
      this.keeperDist = reconstructDistribution(this.playData.archetype, record.shots, this.moodDelta);
      // Plan #9 / peer-review #4: a bfcache freeze mid-dive would leave the
      // sprite stuck on a 'dive-*' texture at the dive destination after
      // restore. Reset before re-emit so the new shot loop starts clean.
      // `this.scale` is a Phaser-runtime property; guard defensively so the
      // existing unit-test stub (which omits `scale`) keeps passing.
      if (this.scale) {
        const { width, height } = this.scale;
        this.keeperSprite?.setPose('ready');
        this.keeperSprite?.setPosition(width / 2, height * 0.30);
      }
      gameBus.emit({
        type: 'scene:enter',
        payload: {
          scene: 'PLAY',
          date: this.playData.date,
          puzzleNumber: puzzleNumber(this.playData.date),
          archetype: this.playData.archetype,
          record,
        },
      });
      // Deferred codex fix: scene:enter ran resetSceneScoped (hideHeatmap=false,
      // gameMode='daily'); re-emit play:mode so a restored hard/practice PlayScene
      // keeps its mode/hideHeatmap signals (matches create() ordering).
      gameBus.emit({
        type: 'play:mode',
        payload: { mode: this.config?.mode ?? 'daily', hideHeatmap: this.config?.hideHeatmap ?? false },
      });
      this.resubscribeToCommands();
      this.practiceRevealed = false; // transient: reveal stamp is durable via prefs/record
      this.recomputeAssist();
    };
    window.addEventListener('pageshow', this.pageShowHandler as EventListener);
    this.events.once('shutdown', () => this.unregisterPageShow());
  }

  private unregisterPageShow(): void {
    if (!this.pageShowHandler) return;
    window.removeEventListener('pageshow', this.pageShowHandler as EventListener);
    delete this.pageShowHandler;
  }

  private resubscribeToCommands(): void {
    // bfcache restore does NOT re-run create() — call this from create() AND the
    // pageshow handler so assist taps survive a restore (Plan #1 §3.4).
    this.commandUnsub?.();
    this.commandUnsub = uiCommandBus.subscribe((c: UiCommand) => {
      if (c.type === 'assist:reveal') this.revealHint();
      else if (c.type === 'practice:exit') this.exitPractice();
    });
    // NOTE: the 'shutdown' cleanup is registered ONCE in create() (not here) —
    // resubscribeToCommands also runs on each bfcache pageshow, so registering a
    // shutdown listener here would accumulate one per restore. The create()
    // handler reads `this.commandUnsub` at shutdown time, so it always unsubs the
    // latest subscription.
  }

  /**
   * Practice's "Back to result" early-exit (the banner button, §4.2 D6).
   * PlayScene — the ACTIVE scene during a practice round — owns this command:
   * ResultScene launched practice via `scene.start('PlayScene', …)`, which shut
   * ResultScene down and unsubscribed it, so it can no longer receive the tap.
   * Mirrors the practice-COMPLETE transition (runShot, §4.2) but re-reads the
   * authoritative daily record from the store to guard a midnight rollover,
   * falling back to the returnTo snapshot.
   */
  private exitPractice(): void {
    if (this.config?.mode !== 'practice' || !this.returnTo) return;
    const record = this.store.getDailyRecord(this.playData.date) ?? this.returnTo.record;
    this.scene.start('ResultScene', {
      date: this.playData.date,
      record,
      streak: this.store.getStreak(),
    });
  }

  private assistAlreadyRevealedThisRound(): boolean {
    if (this.config.submitToD1) {
      return assistUsedToday(() => this.uiPrefs?.getAssistRevealDate() ?? null, this.playData.date);
    }
    return this.practiceRevealed;
  }

  private recomputeAssist(): void {
    if (!this.config) return; // test-stub guard: callout-test stub bypasses init() (no config)
    const available = assistEligible({
      assistEnabled: this.config.assistEnabled,
      shotsThisRound: this.currentShotIndex,
      alreadyRevealedThisRound: this.assistAlreadyRevealedThisRound(),
    });
    gameBus.emit({ type: 'play:assist', payload: { available, hint: null } });
  }

  private revealHint(): void {
    if (this.state !== 'IDLE') return;
    if (!assistEligible({
      assistEnabled: this.config.assistEnabled,
      shotsThisRound: this.currentShotIndex,
      alreadyRevealedThisRound: this.assistAlreadyRevealedThisRound(),
    })) return;
    const hint = lowestProbZone(this.keeperDist);
    if (this.config.submitToD1) {
      // canonical daily: durable forfeit written NOW + the 1/round cap signal.
      this.store.markAssistUsed(this.playData.date);
      this.uiPrefs?.setAssistRevealDate(this.playData.date);
    } else {
      this.practiceRevealed = true; // practice: scene-local, never stamps prefs/store
    }
    gameBus.emit({ type: 'play:assist', payload: { available: false, hint } });
  }

  // test-only: bypass Phaser create() so unit tests can drive the handler directly.
  // Tests MUST call __unregisterPageShowForTests() in afterEach.
  private __registerPageShowForTests(): void { this.registerPageShow(); }
  private __unregisterPageShowForTests(): void { this.unregisterPageShow(); }

  // -------- UI helpers --------

  private drawIndicatorStrip(width: number, height: number): void {
    const y = height * 0.10;
    const total = SHOTS_PER_DAY;
    const w = 24;
    const gap = 8;
    const stripW = total * w + (total - 1) * gap;
    const startX = (width - stripW) / 2 + w / 2;
    for (let i = 0; i < total; i += 1) {
      const x = startX + i * (w + gap);
      const prior = this.playData.existingShots[i];
      let fill = COLOR_PENDING;
      if (prior) fill = prior.outcome === 'goal' ? COLOR_GOAL : COLOR_MISS;
      const rect = this.add.rectangle(x, y, w, w, fill, 1).setStrokeStyle(1, 0xffffff);
      this.indicatorRects.push(rect);
    }
  }

  private drawGoalArea(width: number, height: number): void {
    const goalW = width * 0.86;
    const goalH = height * 0.34;
    const goalY = height * 0.30;
    const goalX = width / 2;
    this.add
      .rectangle(goalX, goalY, goalW, goalH, 0x000000, 0)
      .setStrokeStyle(3, 0xffffff);

    // 6 translucent zones (3 cols × 2 rows). top row = Above; bottom = Front.
    const zoneW = goalW / 3;
    const zoneH = goalH / 2;
    for (let row = 0; row < 2; row += 1) {
      for (let col = 0; col < 3; col += 1) {
        const zx = goalX - goalW / 2 + zoneW / 2 + col * zoneW;
        const zy = goalY - goalH / 2 + zoneH / 2 + row * zoneH;
        this.add
          .rectangle(zx, zy, zoneW - 4, zoneH - 4, COLOR_ZONE, 0.05)
          .setStrokeStyle(1, COLOR_ZONE, 0.3);
      }
    }

    // Plan #9: keeper sprite replaces the orange rect. Source 16x24 grayscale,
    // tinted at runtime to ARCHETYPE_KIT_COLORS[archetype.id]. Uniform scale
    // keyed to goal height preserves the figure's 1:1.5 proportions across
    // viewports (T30 smoke fix — legacy 28×(goalH·0.6) bbox produced a 1:7
    // stick-shape; see Plan #9 §13 catches).
    const targetH = goalH * 0.5;
    const scale = targetH / 24;
    this.keeperSprite = new KeeperSprite(this, goalX, goalY);
    this.keeperSprite.setScale(scale, scale);
    this.keeperSprite.setArchetype(this.playData.archetype.id);
  }

  private drawBall(width: number, height: number): void {
    const x = width / 2;
    const y = height * 0.78;
    // Depth 20 sits above KeeperSprite (depth 10) so saves read as "ball lands
    // on/in the keeper" instead of vanishing behind the silhouette.
    this.ballCircle = this.add.circle(x, y, 28, COLOR_BALL, 1).setDepth(20);
  }

  // -------- Swipe input --------

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.state !== 'IDLE') return;
    // Only accept pointer-down inside the bottom 30% of the screen (ball/field area).
    if (pointer.y < this.scale.height * 0.65) return;
    this.aimStart = { x: pointer.x, y: pointer.y };
    this.aimStartTime = pointer.downTime ?? performance.now();
    this.setPlayState('AIMING');
    this.aimLine = this.add.graphics().setDepth(50);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.state !== 'AIMING' || !this.aimStart || !this.aimLine) return;
    this.aimLine.clear();
    this.aimLine.lineStyle(3, 0xffffff, 0.6);
    this.aimLine.beginPath();
    this.aimLine.moveTo(this.aimStart.x, this.aimStart.y);
    this.aimLine.lineTo(pointer.x, pointer.y);
    this.aimLine.strokePath();
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (this.state !== 'AIMING' || !this.aimStart) return;
    const dx = pointer.x - this.aimStart.x;
    const dy = pointer.y - this.aimStart.y;
    const r = Math.sqrt(dx * dx + dy * dy);
    const durationMs = (pointer.upTime ?? performance.now()) - this.aimStartTime;
    this.clearAim();
    if (r < PlayScene.MIN_POWER_PX || dy >= 0) {
      this.setPlayState('IDLE');
      return;
    }
    this.setPlayState('RESOLVING');
    const vector: SwipeVector = { dx, dy, durationMs, startInGoal: true };
    void this.runShot(vector);
  }

  /** Synthesize an in-memory DailyRecord for practice — never persisted, never POSTed. */
  private buildPracticeRecord(): DailyRecord {
    return {
      schemaVersion: SCHEMA_VERSION,
      shots: [...this.practiceShots],
      score: this.practiceShots.filter((s) => s.outcome === 'goal').length,
      shareGenerated: false,
    };
  }

  private async runShot(vector: SwipeVector): Promise<void> {
    audio().play('shot');
    const swipe = aimToZone(vector, this.prng);
    const keeperZone = sampleKeeperZone(this.keeperDist, this.prng);
    const outcome = resolveShot(swipe.actualZone, keeperZone);

    // Plan #8 (was Plan #5 §3.2): mood-shift now fires on every attempt
    // EXCEPT 'off' (the ball went wide; no target zone to bias toward).
    // Keyed on the player's actualZone — the keeper biases toward zones
    // the player has been targeting. Apply BEFORE the next shot is sampled.
    if (swipe.actualZone !== 'off') {
      this.keeperDist = applyMoodShift(this.keeperDist, swipe.actualZone, this.moodDelta);
    }

    const shotResult: ShotResult = {
      targetZone: swipe.intendedZone === 'off' ? 'CA' : swipe.intendedZone,
      actualZone: swipe.actualZone,
      keeperZone,
      outcome,
    };

    audio().play(outcome === 'goal' ? 'goal' : outcome === 'saved' ? 'save' : 'off');

    await this.runShotAnimation(swipe, vector, keeperZone, outcome);

    // Phase 2 gating: practice never touches the store. writesRecord=false
    // pushes to an in-memory shot log and synthesizes an ephemeral record;
    // the authoritative daily record stays untouched so the share grid and
    // streak can never leak a practice round (§4.2). Optional-chain config so
    // the init-bypassing callout-test stub (no `config`) defaults to the daily
    // write path — `config?.writesRecord !== false` is true when config is absent.
    let record: DailyRecord;
    if (this.config?.writesRecord !== false) {
      // Persist BEFORE emit (design §3.2 rule 3).
      record = this.store.appendShot(this.playData.date, shotResult);
      // Plan gap fix (PRESERVED): persist the hard-mode tag on the authoritative
      // record so the 🕶️ marker/badge (deriveShareMarker reads record.mode) and
      // BootScene resume see hard mode end-to-end. Stamps once.
      if (this.config?.mode === 'daily-hard' && record.mode !== 'daily-hard') {
        this.store.markMode(this.playData.date, this.config.mode);
      }
    } else {
      this.practiceShots.push(shotResult);
      record = this.buildPracticeRecord();
    }

    gameBus.emit({
      type: 'play:shot-resolved',
      payload: { index: record.shots.length - 1, shot: shotResult, record },
    });

    // Plan #1 §4.3: clear the stale hint ring on every shot resolution; the
    // fresh `available` is re-emitted by recomputeAssist() once state returns to IDLE.
    gameBus.emit({ type: 'play:assist', payload: { available: false, hint: null } });

    // Plan #5 §4.2: increment per-zone observation count and emit the snapshot.
    // Spread to avoid mutation aliasing across subscribers.
    this.observations[keeperZone]++;
    gameBus.emit({
      type: 'play:observations-update',
      payload: { observations: { ...this.observations } },
    });

    // After the increment, count === 1 means this is the FIRST reveal of this
    // cell — per spec §4.2 we skip the pulse on first reveal (the cell is just
    // transitioning from hatched-? to its first count, and pulsing on a freshly
    // revealed cell is incoherent). Goals on subsequent visits do pulse.
    const isFirstReveal = this.observations[keeperZone] === 1;
    if (outcome === 'goal' && !isFirstReveal) {
      gameBus.emit({ type: 'play:pulse-zone', payload: { zone: keeperZone } });
      this.pulseTimer?.remove();
      this.pulseTimer = this.time.delayedCall(500, () => {
        gameBus.emit({ type: 'play:pulse-zone', payload: { zone: null } });
      });
    }

    this.events.emit('shot-resolved', {
      shot: shotResult,
      shotIndex: record.shots.length - 1,
      shots: record.shots,
    });

    // Update indicator strip in-scene.
    const lastIdx = record.shots.length - 1;
    const rect = this.indicatorRects[lastIdx];
    if (rect) {
      rect.setFillStyle(outcome === 'goal' ? COLOR_GOAL : COLOR_MISS);
    }
    this.currentShotIndex = record.shots.length;

    if (isComplete(record)) {
      // Default-on when config is absent (init-bypassing test stub) so the
      // daily engagement path is unchanged; practice (affectsStreak=false) skips.
      if (this.config?.affectsStreak !== false) {
        this.store.applyEngagementForDate(this.playData.date);
      }
      this.setPlayState('COMPLETE');
      // Plan #5 §4.8: synchronously clear any in-flight pulse before the scene
      // transition so subscribers don't receive a stale {zone: null} after the
      // ResultScene has mounted.
      this.pulseTimer?.remove();
      gameBus.emit({ type: 'play:pulse-zone', payload: { zone: null } });
      if (this.config?.mode === 'practice' && this.returnTo) {
        // Practice never builds a practice ResultScene — it returns to the
        // authoritative daily result via returnTo (§4.2).
        this.scene.start('ResultScene', this.returnTo);
      } else {
        this.scene.start('ResultScene', {
          date: this.playData.date,
          record,
          streak: this.store.getStreak(),
        });
      }
    } else {
      this.setPlayState('BETWEEN_SHOTS');
      this.time.delayedCall(250, () => { this.setPlayState('IDLE'); this.recomputeAssist(); });
    }
  }

  private runShotAnimation(
    swipe: ReturnType<typeof aimToZone>,
    vector: SwipeVector,
    keeperZone: Zone,
    outcome: ReturnType<typeof resolveShot>,
  ): Promise<void> {
    return new Promise((resolve) => {
      const { width, height } = this.scale;
      const ballEnd = ballTarget(swipe, vector, { width, height });
      const keeperPos = this.zonePosition(keeperZone);

      const showOutcome = (after: () => void): void => {
        const label = t(outcome === 'goal' ? 'outcome.goal' : outcome === 'saved' ? 'outcome.saved' : 'outcome.miss');
        const color = outcome === 'goal' ? '#22cc55' : '#ff5555';
        const txt = this.add
          .text(width / 2, height * 0.5, label, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '40px',
            color,
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setDepth(100);
        this.time.delayedCall(SHOT_TIMING_MS.outcomeFlash, () => {
          txt.destroy();
          this.ballCircle.setPosition(width / 2, height * 0.78);
          this.keeperSprite.setPosition(width / 2, height * 0.30);
          this.keeperSprite.setPose('ready');
          after();
        });
      };

      if (this.motionScale === 0) {
        // Reduced motion: snap both objects to their resolved positions so the
        // keeper's chosen zone is visible (Bayesian-loop legibility), hold briefly,
        // then reset for the next shot. No outcome-flash text — the indicator strip
        // and header counter convey the result; flashing text itself is "motion."
        this.keeperSprite.setPose(keeperZone);
        this.ballCircle.setPosition(ballEnd.x, ballEnd.y);
        this.keeperSprite.setPosition(keeperPos.x, keeperPos.y);
        this.time.delayedCall(SHOT_TIMING_MS.reducedMotionHold, () => {
          this.ballCircle.setPosition(width / 2, height * 0.78);
          this.keeperSprite.setPosition(width / 2, height * 0.30);
          this.keeperSprite.setPose('ready');
          resolve();
        });
        return;
      }
      // Sequential reveal (spec §Q5: non-negotiable Bayesian-loop legibility):
      //   1. Keeper dives to its sampled zone — committed in isolation so the
      //      player registers it before any ball motion competes for attention.
      //   2. Ball flies to its resolved zone (or off-frame target) — arrival
      //      resolves the duel against the now-stationary keeper.
      //   3. Outcome flash overlays the result.
      // Pose key: keeperZone (where the keeper goes), not actualZone (where the
      // ball goes). On saved shots these differ; the dive *pose* must match the
      // dive *position*. Peer-review finding #2 (spec §8).
      this.keeperSprite.setPose(keeperZone);
      this.tweens.add({
        targets: this.keeperSprite,
        x: keeperPos.x,
        y: keeperPos.y,
        duration: SHOT_TIMING_MS.keeperDive,
        ease: 'Cubic.easeOut',
        onComplete: () => {
          this.tweens.add({
            targets: this.ballCircle,
            x: ballEnd.x,
            y: ballEnd.y,
            duration: SHOT_TIMING_MS.ballFlight,
            ease: 'Quad.easeOut',
            onComplete: () => showOutcome(resolve),
          });
        },
      });
    });
  }

  private zonePosition(zone: Zone): { x: number; y: number } {
    const { width, height } = this.scale;
    const goalW = width * 0.86;
    const goalH = height * 0.34;
    const goalX = width / 2;
    const goalY = height * 0.30;
    const zoneW = goalW / 3;
    const zoneH = goalH / 2;
    const colMap: Record<'L' | 'C' | 'R', number> = { L: 0, C: 1, R: 2 };
    const col = colMap[zone[0] as 'L' | 'C' | 'R'];
    const row = zone[1] === 'A' ? 0 : 1;
    return {
      x: goalX - goalW / 2 + zoneW / 2 + col * zoneW,
      y: goalY - goalH / 2 + zoneH / 2 + row * zoneH,
    };
  }

  private onPointerCancel(): void {
    if (this.state !== 'AIMING') return;
    this.clearAim();
    this.setPlayState('IDLE');
  }

  private clearAim(): void {
    this.aimLine?.destroy();
    delete this.aimLine;
    this.aimStart = null;
  }
}
